import AppBackgroundColor from '@components/app-background-color/AppBackgroundColor';
import  { ContentBlock } from '@layouts/ContentBlocks';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import useBreakpoints from '@ui/hooks/useBreakpoints';
import IconAffirm from '@ui/icons/affirm/black-white-bg.svg';
import { makeSeoData } from 'config/next-seo.config';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import { NextSeo } from 'next-seo';
import React from 'react';

import { database } from '../../_app/firebase';
import HowItWorks from '../../components/blocks/HowItWorks';
import { CTAButton } from '../../components/buttons/CTAButton';
import SimpleCard from '../../components/cards/SimpleCard';
import HomesCarousel from '../../components/carousels/HomesCarousel';
import HeroAlt from '../../components/hero-alt/HeroAlt';
import Testimonials from '../../components/testimonials/Testimonials';
import useStyles from './Agents.style';


const title = 'Agent Partnerships';
const description = 'We partner with top real estate agents to bring you the best possible service.';
const image = '/images/agents/hero.png';

export const getServerSideProps: GetServerSideProps = async () => {
  const homes = await getDocs(query(collection(database, 'homes')
    ,where('published', '==', true)
    ,where('featured', '==', true)
    ,orderBy('install_date', 'desc')
    ,limit(10)));

  const data = {
    homes: JSON.stringify(homes.docs.map(h => h.data())),
  };

  return { props: { data } };
};

function Agents(props) {
  const { theme } = useStyles();
  const { md } = useBreakpoints();
  const homes = JSON.parse(props.data.homes) as guesthouse.Home[];

  return (
    <>
      <AppBackgroundColor
        backgroundColor="#F7F7F7"
        borderColor="#F7F7F7"
        headerColor="#F7F7F7"
      />

      <Head>
        <title>
          {title}
        </title>
      </Head>

      <NextSeo {...makeSeoData({ pageTitle: title, metaTitle: title, metaDescription: description, image })} />

      <HeroAlt 
        title={
          <>
            Home prep built for
            {' '}
            <br />
            {' '}
            real estate agents
          </>
          }
        subText={
          <>
            Guest House makes it easy to stand out with premium staging, cleaning, photography and more. Sell faster and deliver exceptional service with every listing.
          </>
          }
        backgroundImage="/images/agents/hero.jpg"
        ctaButtons={[
          {
            label: 'Get an instant quote',
            href: '/home-staging/get-a-quote',
            variant: 'contained',
            color: 'primary',
          },
          { 
            label: 'Speak with our team',
            href: 'https://guesthouseshop-8563089.hs-sites-na2.com/book-now',
            variant: 'contained',
            color: 'secondary',
            target: '_blank',
          },
        ]}
      />
      
      <ContentBlock
        padding={md ? [15, 0, 15, 0] : [4, 0]}
        style={{ backgroundColor: '#E9E6E3' }}
        margin={[0]}
        maxWidth={'none'}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            display={md ? 'flex' : 'block'}
            alignItems="flex-start"
            justifyContent="center"
            gap={10}
            sx={{
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box
              flex={1}
              sx={{
                marginBottom: '32px',
                [theme.breakpoints.up('md')]: {
                  marginBottom: 0,
                },
              }}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: '24px' }}
              >
                Partner with Guest House as a Premier Agent
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{ fontSize: md ? 24 : 18, marginBottom: '32px', lineHeight: 1.4 }}
              >
                Guest House partners with top real estate agents across leading brokerages like Compass, Sotheby’s, The Agency and Douglas Elliman to provide the most impactful listing prep services – delivered in 3 days or less.
              </Typography>
              <a
                href="https://guesthouseshop-8563089.hs-sites-na2.com/book-now"
                style={{
                  display: 'inline-block',
                  fontWeight: 'bold',
                  backgroundColor: '#080808',
                  color: '#ffffff',
                  textDecoration: 'none !important',
                  borderRadius: '30px',
                  padding: md ? '16px 24px' : '12px 16px',
                  lineHeight: 1.4,
                  height: 'auto',
                  marginRight: '16px',
                  fontSize: md ? '20px' : '14px',
                  border: 0,
                }}
                target="_blank"
                rel="noreferrer"
              >
                Talk with our team
              </a>
             
            </Box>
            <Box flex={1}>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{ fontSize: md ? 24 : 18, marginBottom: '32px', lineHeight: 1.4 }}
              >
                Sell faster and earn more with Guest House:
              </Typography>
              
              <SimpleCard
                title={
                  <b>
                    Unlock exclusive discounts
                  </b>
                }
                description=""
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Retention & recruiting"
              />
              <SimpleCard
                title={
                  <b>
                    Access priority scheduling
                  </b>
                }
                description=""
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Brand differentiation"
              />
              <SimpleCard
                title={
                  <b>
                    Get data & materials to grow your business
                  </b>
                }
                description=""
                imageUrl="/images/homepage/chart-circle-icon.png"
                imageAlt="Faster sales in tough markets"
              />
            </Box>
              
          </Box>
          
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        margin={[0]}
        padding={md ? [12, 0] : [6, 0]}
        style={{ backgroundColor: '#F7F7F7' }}
      >
        <ContentBlock
          maxWidth={1400}
          margin={[0, 'auto']}
          padding={[0, 2]}
        >
          <Typography
            fontFamily={theme.gh_vars.circular}
            variant="h2"
            style={{ fontSize: md ? 48 : 24, letterSpacing: '-2%', marginBottom: '64px', textAlign: 'center' }}
          >
            Why prep your next listing with Guest House?
          </Typography>

          <Grid
            container
            spacing={md ? 4 : 3}
            style={{ marginBottom: md ? '64px' : '48px' }}
          >
            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Image
                  src="/images/icons/painting.svg"
                  alt="3-day turnaround"
                  width={md ? 48 : 32}
                  height={md ? 48 : 32}
                  style={{ marginBottom: theme.spacing(3) }}
                />
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="h3"
                  style={{ 
                    fontSize: md ? 20 : 18, 
                    fontWeight: 600, 
                    marginBottom: theme.spacing(1.5) 
                  }}
                >
                  3-day turnaround:
                </Typography>
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  style={{ fontSize: md ? 18 : 16, lineHeight: 1.5 }}
                >
                  From empty to market-ready in record time
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Image
                  src="/images/icons/tools.svg"
                  alt="Faster sales"
                  width={md ? 48 : 32}
                  height={md ? 48 : 32}
                  style={{ marginBottom: theme.spacing(3) }}
                />
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="h3"
                  style={{ 
                    fontSize: md ? 20 : 18, 
                    fontWeight: 600, 
                    marginBottom: theme.spacing(1.5) 
                  }}
                >
                  Faster sales:
                </Typography>
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  style={{ fontSize: md ? 18 : 16, lineHeight: 1.5 }}
                >
                  Our staged listings get offers in less than 30 days on average
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Image
                  src="/images/icons/web-browser.svg"
                  alt="Full service listing prep"
                  width={md ? 48 : 32}
                  height={md ? 48 : 32}
                  style={{ marginBottom: theme.spacing(3) }}
                />
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="h3"
                  style={{ 
                    fontSize: md ? 20 : 18, 
                    fontWeight: 600, 
                    marginBottom: theme.spacing(1.5) 
                  }}
                >
                  Full-service:
                </Typography>
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  style={{ fontSize: md ? 18 : 16, lineHeight: 1.5 }}
                >
                  Move-out, cleaning, staging, photos — handled in one place
                </Typography>
              </Box>
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              md={3}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  height: '100%',
                }}
              >
                <Image
                  src="/images/icons/web-browser.svg"
                  alt="Proven results"
                  width={md ? 48 : 32}
                  height={md ? 48 : 32}
                  style={{ marginBottom: theme.spacing(3) }}
                />
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="h3"
                  style={{ 
                    fontSize: md ? 20 : 18, 
                    fontWeight: 600, 
                    marginBottom: theme.spacing(1.5) 
                  }}
                >
                  Proven results:
                </Typography>
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  variant="body1"
                  style={{ fontSize: md ? 18 : 16, lineHeight: 1.5 }}
                >
                  2,000+ homes staged and sold in Colorado, California, and Arizona
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CTAButton
              href="/home-staging/get-a-quote"
              label="Get an instant quote"
              color="primary"
            />
          </Box>
        </ContentBlock>
      </ContentBlock>
      
      
      <Box
        sx={{
          position: 'relative',
          width: '100vw',
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'none',
          height: md ? 600 : 500,
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Background Image */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: 'url(/images/agents/bg.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            zIndex: 0,
          }}
        />

        {/* Dark Overlay */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(8, 8, 8, 0.5)',
            zIndex: 1,
          }}
        />

        {/* Content */}
        <ContentBlock
          maxWidth={1400}
          margin={[0, 'auto']}
          padding={[0, 2]}
          style={{
            position: 'relative',
            zIndex: 2,
            textAlign: 'center',
          }}
        >
          <Typography
            fontFamily={theme.gh_vars.circular}
            variant="h2"
            style={{
              fontSize: md ? 56 : 32,
              letterSpacing: '-2%',
              marginBottom: md ? theme.spacing(4) : theme.spacing(3),
              color: '#ffffff',
              fontWeight: 500,
            }}
          >
            Flexibility that works with the market
          </Typography>

          <Typography
            fontFamily={theme.gh_vars.circular}
            variant="body1"
            style={{
              fontSize: md ? 24 : 18,
              lineHeight: 1.5,
              marginBottom: md ? theme.spacing(5) : theme.spacing(4),
              color: '#ffffff',
              maxWidth: md ? 800 : '100%',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            Need staging ASAP? Our team can deliver in as little as soon as 3 days for qualifying listings. Perfect for last-minute deals or competitive launches.
          </Typography>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <CTAButton
              href="/home-staging/get-a-quote"
              label="Get an instant quote"
              color="secondary"
            />
          </Box>
        </ContentBlock>
      </Box>

      <ContentBlock
        maxWidth={'none'}
        margin={[0]}
        padding={md ? [12, 0] : [6, 0]}
        style={{ backgroundColor: '#F0F0F0' }}
      >
        <ContentBlock
          maxWidth={1400}
          margin={[0, 'auto']}
          padding={[0, 2]}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: md ? 'row' : 'column',
              gap: md ? 6 : 4,
              alignItems: 'flex-start',
            }}
          >
            {/* Left Column - Text Content */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{
                  fontSize: md ? 48 : 32,
                  letterSpacing: '-2%',
                  marginBottom: theme.spacing(3),
                  color: '#080808',
                  fontWeight: 500,
                }}
              >
                Finance home prep with Affirm
              </Typography>

              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{
                  fontSize: md ? 20 : 18,
                  lineHeight: 1.5,
                  marginBottom: theme.spacing(4),
                  color: '#080808',
                }}
              >
                Stage now, pay later — as low as
                {' '}
                <strong>
                  $175/month
                </strong>
                {' '}
                for 12 months at
                {' '}
                <strong>
                  0% APR*
                </strong>
                .
              </Typography>

              {/* Benefits List */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.spacing(3),
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing(2),
                  }}
                >
                  <Image
                    src="/images/icons/painting.svg"
                    alt=""
                    width={24}
                    height={24}
                    style={{ marginTop: '4px', flexShrink: 0 }}
                  />
                  <Typography
                    fontFamily={theme.gh_vars.circular}
                    variant="body1"
                    style={{
                      fontSize: md ? 18 : 16,
                      lineHeight: 1.5,
                      color: '#080808',
                    }}
                  >
                    Keep your cash flow flexible while still getting your listing market-ready.
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing(2),
                  }}
                >
                  <Image
                    src="/images/icons/painting.svg"
                    alt=""
                    width={24}
                    height={24}
                    style={{ marginTop: '4px', flexShrink: 0 }}
                  />
                  <Typography
                    fontFamily={theme.gh_vars.circular}
                    variant="body1"
                    style={{
                      fontSize: md ? 18 : 16,
                      lineHeight: 1.5,
                      color: '#080808',
                    }}
                  >
                    Finance a base staging package for as low as $175/month based on a full price of $2,100 (down payment may be required).
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: theme.spacing(2),
                  }}
                >
                  <Image
                    src="/images/icons/painting.svg"
                    alt=""
                    width={24}
                    height={24}
                    style={{ marginTop: '4px', flexShrink: 0 }}
                  />
                  <Typography
                    fontFamily={theme.gh_vars.circular}
                    variant="body1"
                    style={{
                      fontSize: md ? 18 : 16,
                      lineHeight: 1.5,
                      color: '#080808',
                    }}
                  >
                    0% APR based on a 12-month term. Subject to approval. See Affirm for details.
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Right Column - Financing Card */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
              }}
            >
              <Box
                sx={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: md ? theme.spacing(4) : theme.spacing(3),
                  width: '100%',
                }}
              >
                {/* Partnership Logos */}
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent={'center'}
                  alignItems="baseline"
                  marginBottom={md ? theme.spacing(3) : theme.spacing(3)}
                >
                  <IconAffirm style={{ height: md ? 48 : 40, width: md ? 116 : 100 }} />
                  <Typography
                    style={{
                      fontSize: md ? 26 : 14,
                      opacity: 0.6,
                      margin: theme.spacing(0, 1.5)
                    }}
                  >
                    +
                  </Typography>
                  <Typography
                    style={{
                      fontSize: md ? 36 : 24,
                      fontWeight: 600
                    }}
                  >
                    Guest House
                  </Typography>
                </Box>

                {/* Financing Summary */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    paddingY: theme.spacing(3),
                    marginBottom: theme.spacing(3),
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      borderRight: '1px solid #000000',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                    }}
                  >
                    <Typography
                      fontFamily={theme.gh_vars.circular}
                      style={{
                        fontSize: md ? 36 : 28,
                        fontWeight: 600,
                        color: '#080808',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      $175
                    </Typography>
                    <Typography
                      fontFamily={theme.gh_vars.circular}
                      style={{
                        fontSize: md ? 14 : 12,
                        color: '#080808',
                      }}
                    >
                      Per month
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                      borderRight: '1px solid #000000',
                    }}
                  >
                    <Typography
                      fontFamily={theme.gh_vars.circular}
                      style={{
                        fontSize: md ? 36 : 28,
                        fontWeight: 600,
                        color: '#080808',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      12
                    </Typography>
                    <Typography
                      fontFamily={theme.gh_vars.circular}
                      style={{
                        fontSize: md ? 14 : 12,
                        color: '#080808',
                      }}
                    >
                      Months
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      flex: 1,
                      textAlign: 'center',
                    }}
                  >
                    <Typography
                      fontFamily={theme.gh_vars.circular}
                      style={{
                        fontSize: md ? 36 : 28,
                        fontWeight: 600,
                        color: '#080808',
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      0%
                    </Typography>
                    <Typography
                      fontFamily={theme.gh_vars.circular}
                      style={{
                        fontSize: md ? 14 : 12,
                        color: '#080808',
                      }}
                    >
                      APR*
                    </Typography>
                  </Box>
                </Box>

                {/* Disclaimer */}
                <Typography
                  fontFamily={theme.gh_vars.circular}
                  style={{
                    fontSize: md ? 12 : 11,
                    lineHeight: 1.4,
                    color: '#666666',
                    marginBottom: theme.spacing(),
                    textAlign: 'center',
                  }}
                >
                  *Finance a base staging package with Affirm for as low as $175 over 12-months at 0% APR based on full price of $2100 (down payment may be required).
                </Typography>

                {/* CTA Button */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: theme.spacing(6),
                  }}
                >
                  <CTAButton
                    href="/home-staging/get-a-quote"
                    label="Get an instant quote"
                    color="primary"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </ContentBlock>
      </ContentBlock>

      <HowItWorks />

      <ContentBlock
        margin={[0]}
        padding={[4, 0, 4, 0]}
        paddingMd={[15, 0, 15, 0]}
        style={{
          backgroundColor: '#CCD5D6'
        }}
        maxWidth={'none'}
      >
        <ContentBlock
          margin={[0, 'auto']}
          
          maxWidth={1308}
        >
          <Typography
            variant="body1"
            component="h2"
            style={{ fontSize: md ? 20 : 16,marginBottom: 8 }}
          >
            Reviews
          </Typography>
          <Typography
            variant="h4Alt"
            style={{ fontSize: md ? 48 : 28, marginBottom: md ? theme.spacing(5) : theme.spacing(3) }}
          >
            What customers are saying
          </Typography>
         
          <Testimonials
            backgroundColor="#ffffff"
            location="denver"
          />
         
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        style={{ backgroundColor: '#f6f6f7', overflowX: 'hidden' }}
        padding={[4, 0, 6, 0]}
        paddingMd={[12, 0, 12, 0]}
        margin={0}
      >
        <ContentBlock
          maxWidth={1308}
          margin={[0, 'auto']}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              '& a': {
                textDecoration: 'none !important'
              }
            }}
          >
            <Box>
              <Typography
                variant="h4Alt"
                style={{ fontSize: md ? 48 : 28, marginBottom: 8 }}
              >
                Recent listings
                {' '}
              </Typography>
         
              <Typography
                variant="body1"
                component="h2"
                style={{ fontSize: md ? 20 : 16, marginBottom: 60 }}
              >
                Check out our work in Colorado, California, Arizona & beyond
                {' '}
              </Typography>
            </Box>
            <CTAButton
              href="/shop/homes"
              label="See all listings"
              color="primary"
            />
          </Box>

          {!!homes?.length && (
            <HomesCarousel
              seeAll
              homes={homes}
            />
          )}
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        margin={[0]}
        padding={md ? [12, 0] : [6, 0]}
        style={{ backgroundColor: '#EEECEA' }}
      >
        <ContentBlock
          maxWidth={1400}
          margin={[0, 'auto']}
          padding={[0, 2]}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: md ? 'row' : 'column',
              gap: md ? 6 : 4,
              alignItems: 'flex-start',
            }}
          >
            {/* Left Column - Text Content */}
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="h2"
                style={{
                  fontSize: md ? 48 : 32,
                  letterSpacing: '-2%',
                  marginBottom: theme.spacing(3),
                  color: '#080808',
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                Get the Guide: 5 Reasons to Stage in 2025
              </Typography>
              <Typography
                fontFamily={theme.gh_vars.circular}
                variant="body1"
                style={{
                  fontSize: md ? 20 : 18,
                  lineHeight: 1.5,
                  color: '#080808',
                }}
              >
                Need to convince your seller on staging, or simply want to learn more about why staging is quickly becoming a must-have? Download our free guide.
              </Typography>
            </Box>

            {/* Right Column - Image with Overlaid Button */}
            <Box
              sx={{
                flex: 1,
                position: 'relative',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  width: '100%',
                  height: md ? 400 : 300,
                }}
              >
                <Image
                  src="/images/homepage/instantly-book.jpg"
                  alt="Modern staged living room"
                  width={800}
                  height={600}
                  style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                  }}
                />
                {/* Overlaid Button */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                  }}
                >
                  <CTAButton
                    href="https://www.canva.com/design/DAGtpGBg104/ZCQi8bIBazCHsZhGgsnQFw/view"
                    label="Download our guide"
                    color="primary"
                    target="_blank"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </ContentBlock>
      </ContentBlock>

      <ContentBlock
        maxWidth={'none'}
        margin={[0]}
        padding={md ? [12, 0] : [6, 0]}
        style={{ backgroundColor: theme.palette.common.white }}
      >
        <ContentBlock
          maxWidth={1400}
          margin={[0, 'auto']}
          padding={[0, 2]}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
            }}
          >
            <Typography
              fontFamily={theme.gh_vars.circular}
              variant="h2"
              style={{
                fontSize: md ? 48 : 28,
                letterSpacing: '-2%',
                marginBottom: md ? theme.spacing(5) : theme.spacing(4),
                fontWeight: 450,
                color: theme.palette.common.black,
              }}
            >
              Ready to list faster?
            </Typography>
            <CTAButton
              href="/home-staging/get-a-quote"
              label="Get an instant quote"
              color="primary"
            />
          </Box>
        </ContentBlock>
      </ContentBlock>

    </>
  );
}

export default Agents;
